import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ApiService, { IntegrationLinkError } from '../services/apiService';
import { ConfirmIntegrationLinkResponse, IntegrationLinkDetails, OpenIntegrationLinkResponse } from '../types';
import { KoppelIntent, clearKoppelIntent, readKoppelIntent, writeKoppelIntent } from '../utils/koppelIntent';
import { setPartnerRecordingTarget } from '../utils/partnerRecordingContext';
import { showAppToast } from './AppToastHost';
import { logger } from '../utils/logger';

/**
 * Koppelpagina voor partnerkoppelingen (bv. Praktijkdata). De partner opent
 * /koppelen#<token>; deze pagina claimt de link voor deze browser, laat zo
 * nodig eerst inloggen, toont met welk account er gekoppeld wordt en bevestigt.
 *
 * Het token en de claim staan alleen in sessionStorage (zie koppelIntent) en in
 * JSON-bodies; nooit in een url en nooit in logs.
 */

// A: link onbekend · B: verlopen, gebruikt of in een andere browser geopend · C: tijdelijk niet mogelijk
type ErrorScreen = 'invalid' | 'expired' | 'unavailable';

type Phase =
    | { name: 'loading' }
    // includeUser: stand van het vinkje "ook mijn account" (alleen bij includeUserOffered)
    | { name: 'confirm'; details: IntegrationLinkDetails; busy: boolean; notice: string | null; includeUser: boolean }
    | { name: 'cannot'; details: IntegrationLinkDetails }
    | { name: 'opening' }
    | { name: 'done'; result: ConfirmIntegrationLinkResponse; details: IntegrationLinkDetails }
    | { name: 'error'; screen: ErrorScreen; message: string | null };

const EXPIRED_CODES = ['expired', 'used', 'already_opened', 'invalid_claim', 'credential_missing'];

const errorScreenFor = (error: unknown): ErrorScreen => {
    const code = error instanceof IntegrationLinkError ? error.code : null;
    if (code === 'not_found') return 'invalid';
    if (code && EXPIRED_CODES.includes(code)) return 'expired';
    return 'unavailable';
};

// Een tweede open-aanroep met hetzelfde token geeft already_opened. StrictMode
// voert effects in development twee keer uit, en de pagina rendert opnieuw
// zodra de login klaar is; daarom één gedeelde aanroep per token, die de claim
// meteen bewaart (ook als het component intussen unmount).
const openRequests = new Map<string, Promise<OpenIntegrationLinkResponse>>();

const openOnce = (apiService: ApiService, intent: KoppelIntent): Promise<OpenIntegrationLinkResponse> => {
    let request = openRequests.get(intent.token);
    if (!request) {
        request = apiService.openIntegrationLink(intent.token).then(response => {
            writeKoppelIntent({
                ...intent,
                claim: response.claim,
                kind: response.kind,
                partnerName: response.partnerName,
                expiresAt: response.expiresAt,
            });
            return response;
        });
        openRequests.set(intent.token, request);
    }
    return request;
};

/** Stap A: token uit het fragment naar sessionStorage, en het fragment uit de adresbalk. */
const takeTokenFromUrl = (): KoppelIntent | null => {
    const token = window.location.hash.replace(/^#/, '').trim();
    if (token) {
        const existing = readKoppelIntent();
        if (!existing || existing.token !== token) {
            writeKoppelIntent({ token, openedAt: new Date().toISOString() });
        }
        // Niet in de browsergeschiedenis of op een schermafbeelding
        window.history.replaceState(window.history.state, '', window.location.pathname);
    }
    return readKoppelIntent();
};

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const Spinner: React.FC<{ className?: string }> = ({ className = 'h-5 w-5' }) => (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24" aria-hidden="true">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);

const primaryButtonClass =
    'inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed';
const secondaryButtonClass =
    'px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60';

const KoppelPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isLoading: authLoading, isLoggedIn, logout } = useAuth();
    const apiService = useMemo(() => new ApiService(), []);
    const [phase, setPhase] = useState<Phase>({ name: 'loading' });
    // Voor de foutschermen; bekend na open, of uit de bewaarde intent
    const [partnerName, setPartnerName] = useState<string | null>(() => readKoppelIntent()?.partnerName || null);
    // Een foutscherm of klaar-scherm is eindstation: een nieuwe run (bv. zodra de
    // loginstatus bekend is) mag dat niet meer overschrijven.
    const settledRef = useRef(false);
    // Opent de partner een nieuwe link in een tabblad dat al op deze pagina
    // staat, dan wijzigt alleen het fragment en laadt de pagina niet opnieuw
    const [linkSerial, setLinkSerial] = useState(0);

    const showError = (error: unknown) => {
        settledRef.current = true;
        const screen = errorScreenFor(error);
        if (screen !== 'unavailable') {
            clearKoppelIntent();
        }
        const linkError = error instanceof IntegrationLinkError ? error : null;
        const message = linkError?.message || (linkError?.status === null ? t('common.errors.network') : null);
        if (screen === 'unavailable') {
            // Alleen code en status; nooit token of claim
            logger.error('Koppelpagina: verzoek mislukt', linkError ? `${linkError.status ?? 'netwerk'} ${linkError.code ?? ''}` : error);
        }
        setPhase({ name: 'error', screen, message });
    };

    const finish = (result: ConfirmIntegrationLinkResponse, details: IntegrationLinkDetails) => {
        clearKoppelIntent();
        if (result.kind === 'organization') {
            settledRef.current = true;
            setPhase({ name: 'done', result, details });
            return;
        }
        const ref = result.context?.ref;
        setPartnerRecordingTarget(ref
            ? { configId: result.configId, partnerName: result.partnerName, context: { ...result.context, ref } }
            : null);
        const praktijk = result.tenantName ?? details.tenantName ?? result.partnerName;
        if (result.status !== 'already_linked') {
            showAppToast(t('koppelPage.accountLinked', { praktijk }));
        } else if (!ref) {
            // Zonder afspraak (Koppel mijn Notizy-account) is er geen opnamescherm
            // dat de klik bevestigt; zonder melding lijkt er niets te gebeuren
            showAppToast(details.displayName
                ? t('koppelPage.alreadyLinked', { naam: details.displayName, praktijk })
                : t('koppelPage.alreadyLinkedNoName', { praktijk }));
        }
        navigate('/recordings', { replace: true });
    };

    const confirm = async (
        intent: KoppelIntent,
        details: IntegrationLinkDetails,
        includeUser: boolean,
        isCancelled: () => boolean = () => false,
    ) => {
        try {
            const result = details.includeUserOffered
                ? await apiService.confirmIntegrationLink(intent.token, intent.claim ?? '', includeUser)
                : await apiService.confirmIntegrationLink(intent.token, intent.claim ?? '');
            if (isCancelled()) return;
            finish(result, details);
        } catch (error) {
            if (isCancelled()) return;
            if (error instanceof IntegrationLinkError && error.code === 'conflict') {
                // Gelijktijdige wijziging: opnieuw bevestigen kan gewoon
                setPhase({
                    name: 'confirm',
                    details,
                    busy: false,
                    notice: error.message || t('koppelPage.retryFallback'),
                    includeUser,
                });
                return;
            }
            showError(error);
        }
    };

    useEffect(() => {
        let cancelled = false;
        const isCancelled = () => cancelled;

        const run = async () => {
            if (settledRef.current) return;
            const intent = takeTokenFromUrl();
            if (!intent) {
                settledRef.current = true;
                setPhase({ name: 'error', screen: 'invalid', message: null });
                return;
            }

            // Stap B: één keer openen; na inloggen of verversen staat de claim al klaar
            let claimed = intent;
            if (!intent.claim) {
                try {
                    const opened = await openOnce(apiService, intent);
                    claimed = { ...intent, claim: opened.claim, kind: opened.kind, partnerName: opened.partnerName };
                } catch (error) {
                    if (!cancelled) showError(error);
                    return;
                }
                if (cancelled) return;
            }
            setPartnerName(claimed.partnerName || null);

            // Stap C: inloggen; de inlogflow stuurt daarna terug naar deze pagina
            if (authLoading) return;
            if (!isLoggedIn) {
                navigate('/login', { replace: true });
                return;
            }

            // Stap D: wat er gekoppeld wordt, en door wie
            let details: IntegrationLinkDetails;
            try {
                details = await apiService.getIntegrationLinkDetails(claimed.token, claimed.claim ?? '');
            } catch (error) {
                if (!cancelled) showError(error);
                return;
            }
            if (cancelled) return;
            setPartnerName(details.partnerName || null);

            if (!details.canConfirm) {
                setPhase({ name: 'cannot', details });
                return;
            }
            if (details.kind === 'user' && details.alreadyLinked) {
                // Zelfde koppeling bestaat al: zonder scherm door naar opnemen
                setPhase({ name: 'opening' });
                await confirm(claimed, details, false, isCancelled);
                return;
            }
            setPhase({ name: 'confirm', details, busy: false, notice: null, includeUser: details.includeUserDefault === true });
        };

        void run();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- opnieuw zodra de loginstatus bekend is of wijzigt, of bij een nieuwe link
    }, [authLoading, isLoggedIn, linkSerial]);

    useEffect(() => {
        const handleHashChange = () => {
            if (!window.location.hash.replace(/^#/, '').trim()) return;
            settledRef.current = false;
            setPhase({ name: 'loading' });
            setLinkSerial((serial) => serial + 1);
        };
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    const handleConfirm = () => {
        if (phase.name !== 'confirm' || phase.busy) return;
        const intent = readKoppelIntent();
        if (!intent?.claim) {
            setPhase({ name: 'error', screen: 'expired', message: null });
            return;
        }
        setPhase({ ...phase, busy: true, notice: null });
        void confirm(intent, phase.details, phase.includeUser);
    };

    const handleIncludeUserChange = (includeUser: boolean) => {
        if (phase.name !== 'confirm' || phase.busy) return;
        setPhase({ ...phase, includeUser });
    };

    // Waaraan de praktijk gekoppeld wordt: de organisatie, of zonder organisatie het account zelf
    const ownerLabel = (owner: IntegrationLinkDetails['owner'], organizationName: string | null) =>
        owner === 'account'
            ? t('koppelPage.ownerAccount')
            : organizationName ?? t('koppelPage.fallbackOrganization');

    const handleCancel = () => {
        // Niets naar de backend: de link verloopt vanzelf
        clearKoppelIntent();
        navigate('/recordings', { replace: true });
    };

    const handleToNotizy = () => {
        clearKoppelIntent();
        navigate('/', { replace: true });
    };

    const handleOtherAccount = async () => {
        // Intent bewust laten staan: na inloggen met het juiste account gaat het hier verder
        await logout();
        navigate('/login', { replace: true });
    };

    const accountBlock = (details: IntegrationLinkDetails, disabled = false) => (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
            <p className="break-all">
                <Trans
                    i18nKey="koppelPage.loggedInAs"
                    values={{ email: details.account.email || details.account.username }}
                    components={{ strong: <span className="font-medium text-gray-900" /> }}
                />
            </p>
            <button
                type="button"
                onClick={handleOtherAccount}
                disabled={disabled}
                className="mt-1 text-sm font-medium text-primary-700 underline-offset-2 hover:underline disabled:opacity-60"
            >
                {t('koppelPage.otherAccount')}
            </button>
        </div>
    );

    const renderConfirm = (details: IntegrationLinkDetails, busy: boolean, notice: string | null, includeUser: boolean) => {
        const partner = details.partnerName;
        const isOrganization = details.kind === 'organization';
        let question: string;
        let explanation: string;
        let infoNotice: string | null = null;
        let warningNotice: string | null = null;
        let includeUserLabel: string | null = null;

        if (isOrganization) {
            const praktijk = details.tenantName ?? t('koppelPage.fallbackPractice');
            if (details.owner === 'account') {
                // Zelfstandige praktijk zonder organisatie: de koppeling hoort bij dit account
                question = t('koppelPage.organizationQuestionAccount', { partner, praktijk });
                explanation = t('koppelPage.organizationExplanationAccount', { partner });
            } else {
                question = t('koppelPage.organizationQuestion', {
                    partner,
                    praktijk,
                    organisatie: ownerLabel(details.owner, details.organizationName),
                });
                explanation = t('koppelPage.organizationExplanation', { partner, praktijk });
            }
            if (details.includeUserOffered) {
                const plek = details.tenantName ?? partner;
                includeUserLabel = details.displayName
                    ? t('koppelPage.includeUser', { naam: details.displayName, praktijk: plek })
                    : t('koppelPage.includeUserNoName', { praktijk: plek });
            }
            if (details.movesFromOtherOrganization) {
                warningNotice = t('koppelPage.organizationMoves');
            } else if (details.replacesExisting) {
                infoNotice = t('koppelPage.organizationNewKey');
            }
        } else {
            if (details.displayName && details.tenantName) {
                question = t('koppelPage.accountQuestion', { naam: details.displayName, praktijk: details.tenantName });
            } else if (details.displayName) {
                question = t('koppelPage.accountQuestionNoPractice', { naam: details.displayName });
            } else {
                question = t('koppelPage.accountQuestionNoName', { plek: details.tenantName ?? partner });
            }
            explanation = t('koppelPage.accountExplanation', { partner });
            if (details.replacesLink) {
                warningNotice = details.replacesLink.displayName
                    ? t('koppelPage.accountReplaces', { naam: details.replacesLink.displayName })
                    : t('koppelPage.accountReplacesNoName');
            }
        }

        return (
            <>
                <h1 className="text-lg font-semibold text-gray-900">
                    {isOrganization ? t('koppelPage.titleOrganization') : t('koppelPage.titleAccount')}
                </h1>
                <p className="mt-3 text-sm text-gray-900">{question}</p>
                <p className="mt-2 text-sm text-gray-600">{explanation}</p>
                {infoNotice && (
                    <div className="mt-4 flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-800">
                        <svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p>{infoNotice}</p>
                    </div>
                )}
                {warningNotice && (
                    <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800" role="alert">
                        <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <p>{warningNotice}</p>
                    </div>
                )}
                <div className="mt-5">{accountBlock(details, busy)}</div>
                {includeUserLabel && (
                    <label className="mt-4 flex cursor-pointer items-start gap-2.5 text-sm text-gray-900">
                        <input
                            type="checkbox"
                            checked={includeUser}
                            disabled={busy}
                            onChange={(event) => handleIncludeUserChange(event.target.checked)}
                            className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span>{includeUserLabel}</span>
                    </label>
                )}
                {notice && (
                    <p className="mt-4 text-sm text-red-600" role="alert">{notice}</p>
                )}
                <div className="mt-6 flex justify-end gap-2">
                    <button type="button" onClick={handleCancel} disabled={busy} className={secondaryButtonClass}>
                        {t('common.cancel')}
                    </button>
                    <button type="button" onClick={handleConfirm} disabled={busy} className={primaryButtonClass}>
                        {busy && <Spinner className="h-4 w-4" />}
                        {t('koppelPage.confirm')}
                    </button>
                </div>
            </>
        );
    };

    const renderDone = (result: ConfirmIntegrationLinkResponse, details: IntegrationLinkDetails) => {
        const praktijk = result.tenantName ?? t('koppelPage.fallbackPractice');
        const organisatie = ownerLabel(result.owner ?? details.owner, details.organizationName);
        const text = result.status === 'rotated'
            ? t('koppelPage.doneRotated', { praktijk })
            : result.status === 'moved'
                ? capitalize(t('koppelPage.doneMoved', { praktijk, organisatie }))
                : result.userLink
                    // Vinkje "ook mijn account": praktijk en account in één stap
                    ? capitalize(t('koppelPage.doneLinkedWithAccount', { praktijk }))
                    : capitalize(t('koppelPage.doneLinked', { praktijk, partner: result.partnerName }));
        return (
            <div className="text-center">
                <svg className="mx-auto mb-3 h-9 w-9 text-primary-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <h1 className="text-lg font-semibold text-gray-900">{t('koppelPage.doneTitle')}</h1>
                <p className="mt-2 text-sm text-gray-700">{text}</p>
                <button type="button" onClick={handleToNotizy} className={`${primaryButtonClass} mt-6`}>
                    {t('koppelPage.toNotizy')}
                </button>
            </div>
        );
    };

    const renderError = (screen: ErrorScreen, message: string | null) => {
        const title = screen === 'invalid'
            ? t('koppelPage.invalidTitle')
            : screen === 'expired'
                ? t('koppelPage.expiredTitle')
                : t('koppelPage.unavailableTitle');
        const body = screen === 'invalid'
            ? (partnerName ? t('koppelPage.invalidBody', { partner: partnerName }) : t('koppelPage.invalidBodyGeneric'))
            : screen === 'expired'
                ? (partnerName ? t('koppelPage.expiredBody', { partner: partnerName }) : t('koppelPage.expiredBodyGeneric'))
                : message || t('koppelPage.unavailableFallback');
        return (
            <div className="text-center">
                <svg className="mx-auto mb-3 h-9 w-9 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5M4 4l16 16" />
                </svg>
                <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
                <p className="mt-2 text-sm text-gray-700">{body}</p>
                <button type="button" onClick={handleToNotizy} className={`${primaryButtonClass} mt-6`}>
                    {t('koppelPage.toNotizy')}
                </button>
            </div>
        );
    };

    let content: React.ReactNode;
    switch (phase.name) {
        case 'confirm':
            content = renderConfirm(phase.details, phase.busy, phase.notice, phase.includeUser);
            break;
        case 'cannot':
            content = (
                <>
                    <h1 className="text-lg font-semibold text-gray-900">{t('koppelPage.cannotTitle')}</h1>
                    <p className="mt-3 text-sm text-gray-900">{phase.details.message || t('koppelPage.unavailableFallback')}</p>
                    <div className="mt-5">{accountBlock(phase.details)}</div>
                    <div className="mt-6 flex justify-end">
                        <button type="button" onClick={handleToNotizy} className={primaryButtonClass}>
                            {t('koppelPage.toNotizy')}
                        </button>
                    </div>
                </>
            );
            break;
        case 'done':
            content = renderDone(phase.result, phase.details);
            break;
        case 'error':
            content = renderError(phase.screen, phase.message);
            break;
        default:
            content = (
                <div className="flex flex-col items-center py-6 text-center text-gray-600" role="status">
                    <Spinner className="h-7 w-7 text-primary-600" />
                    <p className="mt-3 text-sm">
                        {phase.name === 'opening' ? t('koppelPage.opening') : t('koppelPage.loading')}
                    </p>
                </div>
            );
    }

    return (
        <div className="min-h-screen bg-background flex flex-col justify-center py-12 px-4 sm:px-6">
            <div className="mx-auto w-full max-w-md">
                <img src="/icons/icon.png" alt="Notizy" className="mx-auto mb-6 h-12 w-12 rounded-xl shadow" />
                <div className="rounded-2xl border border-white/50 bg-white px-5 py-7 shadow-hard sm:px-8">
                    {content}
                </div>
            </div>
        </div>
    );
};

export default KoppelPage;
